import { useEffect, useRef, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { Check, Phone, PhoneForwarded } from 'lucide-react'
import {
  useFormContext,
  useFormState,
  useWatch,
  type Control,
} from 'react-hook-form'
import { DialOutIntent } from '@super/shared-types'
import type { WorkflowWithActionGroups } from '@super/shared-types'
import type { FormValues } from './agent-detail.schema'
import { getDialOutVariables } from './agent-detail.constants'

type DialOutSettingsCardProps = {
  control: Control<FormValues>
  workflows: WorkflowWithActionGroups[]
  /** Variables from the selected Trigger; overrides the triggerType-based list. */
  triggerVariables?: string[]
}

type ActionGroupOption = {
  id: string
  workflowId: string
  workflowName: string
  groupName: string | null
  /** Display label: "Handoff A" (single group) or "Handoff A - GroupName" (multi) */
  label: string
  phones: string[]
}

export function DialOutSettingsCard({
  control,
  workflows,
  triggerVariables,
}: DialOutSettingsCardProps) {
  const { setValue, getValues } = useFormContext<FormValues>()
  const { errors } = useFormState({ control })
  const mode = useWatch({ control, name: 'dialOutSettings.mode' })
  const sectionRef = useRef<HTMLDivElement>(null)

  const hasDialOutError = !!errors.dialOutSettings
  const isFirstError = hasDialOutError && !errors.name && !errors.channels

  useEffect(() => {
    if (isFirstError && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const firstTab =
        sectionRef.current.querySelector<HTMLElement>('[role="tab"]')
      firstTab?.focus()
    }
  }, [isFirstError])

  // Mode change clears the inactive field so ACTION_LIST and TRIGGER_VARIABLE
  // can never be populated simultaneously — the DTO is a discriminated union.
  // Only dirty the form when actually clearing a set value (existing agents
  // load with one field already null and must not appear dirty on mount).
  useEffect(() => {
    if (mode === 'ACTION_LIST') {
      if (getValues('dialOutSettings.variableName')) {
        setValue('dialOutSettings.variableName', null, { shouldDirty: true })
      }
    } else if (mode === 'TRIGGER_VARIABLE') {
      const ids = getValues('dialOutSettings.actionGroupIds')
      if (ids && ids.length > 0) {
        setValue('dialOutSettings.actionGroupIds', [], { shouldDirty: true })
      }
    }
  }, [mode, setValue, getValues])

  const actionGroupOptions = useMemo<ActionGroupOption[]>(() => {
    return workflows.flatMap(wf => {
      const groups = wf.actionGroups ?? []
      const hasMultipleGroups = groups.length > 1
      return groups
        .filter(g => (g.actions ?? []).some(a => a.actionType === 'PHONE_CALL'))
        .map(g => {
          const phones = (g.actions ?? [])
            .filter(a => a.actionType === 'PHONE_CALL')
            .map(a => (a.parameters as { number?: string } | null)?.number)
            .filter((n): n is string => !!n)
          return {
            id: g.id,
            workflowId: wf.id,
            workflowName: wf.name,
            groupName: g.name,
            label:
              hasMultipleGroups && g.name ? `${wf.name} - ${g.name}` : wf.name,
            phones,
          }
        })
    })
  }, [workflows])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <PhoneForwarded className="w-4 h-4" />
          Dial out
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={control}
          name="dialOutSettings.intent"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <label
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                  field.value === DialOutIntent.CONVERSE || !field.value
                    ? 'border-primary/50 bg-primary/5'
                    : 'border-border hover:border-muted-foreground/40',
                )}
              >
                <input
                  type="radio"
                  name="dialOutSettings.intent"
                  value={DialOutIntent.CONVERSE}
                  checked={
                    field.value === DialOutIntent.CONVERSE || !field.value
                  }
                  onChange={() => field.onChange(DialOutIntent.CONVERSE)}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium">
                    Conversation{' '}
                    <span className="text-muted-foreground">(default)</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    The agent has a direct conversation with the recipient
                  </p>
                </div>
              </label>
              <label
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                  field.value === DialOutIntent.NOTIFY
                    ? 'border-primary/50 bg-primary/5'
                    : 'border-border hover:border-muted-foreground/40',
                )}
              >
                <input
                  type="radio"
                  name="dialOutSettings.intent"
                  value={DialOutIntent.NOTIFY}
                  checked={field.value === DialOutIntent.NOTIFY}
                  onChange={() => field.onChange(DialOutIntent.NOTIFY)}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium">
                    Notify and acknowledgement
                  </p>
                  <p className="text-xs text-muted-foreground">
                    The agent delivers a notification and waits for the
                    recipient to acknowledge the call
                  </p>
                </div>
              </label>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormLabel
          className={cn(
            'inline-block mb-1',
            hasDialOutError && 'text-destructive',
          )}
        >
          Phone number source
        </FormLabel>

        <div ref={sectionRef}>
          <FormField
            control={control}
            name="dialOutSettings.mode"
            render={({ field }) => (
              <Tabs
                value={field.value ?? 'ACTION_LIST'}
                onValueChange={field.onChange}
                className="w-full"
              >
                <TabsList
                  className="grid w-full grid-cols-2"
                  mobileDropdown={false}
                >
                  <TabsTrigger value="ACTION_LIST">Action list</TabsTrigger>
                  <TabsTrigger value="TRIGGER_VARIABLE">Variable</TabsTrigger>
                </TabsList>
                <TabsContent value="ACTION_LIST" className="mt-2">
                  <ActionListSection
                    control={control}
                    options={actionGroupOptions}
                  />
                </TabsContent>
                <TabsContent value="TRIGGER_VARIABLE" className="mt-2">
                  <TriggerVariableSection
                    control={control}
                    triggerVariables={triggerVariables}
                  />
                </TabsContent>
              </Tabs>
            )}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function ActionListSection({
  control,
  options,
}: {
  control: Control<FormValues>
  options: ActionGroupOption[]
}) {
  const { setValue, trigger } = useFormContext<FormValues>()
  const actionGroupIds =
    useWatch({
      control,
      name: 'dialOutSettings.actionGroupIds',
    }) ?? []

  const toggle = (id: string) => {
    const next = actionGroupIds.includes(id)
      ? actionGroupIds.filter(x => x !== id)
      : [...actionGroupIds, id]
    setValue('dialOutSettings.actionGroupIds', next, { shouldDirty: true })
    void trigger('dialOutSettings')
  }

  return (
    <FormField
      control={control}
      name="dialOutSettings.actionGroupIds"
      render={() => (
        <FormItem>
          {options.length === 0 ? (
            <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
              No action groups with phone actions found — create them in a
              handoff workflow.
            </div>
          ) : (
            <div
              role="group"
              className="border rounded-md max-h-[400px] overflow-y-auto"
            >
              {options.map(opt => {
                const isSelected = actionGroupIds.includes(opt.id)
                const isDefault =
                  isSelected &&
                  actionGroupIds[0] === opt.id &&
                  actionGroupIds.length > 1
                return (
                  <div
                    key={opt.id}
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={0}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors border-b last:border-b-0',
                      isSelected ? 'bg-primary/5' : 'hover:bg-muted/50',
                    )}
                    onClick={() => toggle(opt.id)}
                    onKeyDown={e => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault()
                        toggle(opt.id)
                      }
                    }}
                  >
                    <div
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded border-2 transition-colors flex-shrink-0',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted-foreground/30',
                      )}
                    >
                      {isSelected && <Check className="h-2.5 w-2.5" />}
                    </div>
                    <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {opt.label}
                        </span>
                        {isDefault && (
                          <span className="text-xs text-muted-foreground shrink-0">
                            (default)
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {opt.phones.join(', ')}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

function TriggerVariableSection({
  control,
  triggerVariables,
}: {
  control: Control<FormValues>
  triggerVariables?: string[]
}) {
  const { trigger, setValue, getValues } = useFormContext<FormValues>()
  const triggerType = useWatch({ control, name: 'triggerType' })
  const hasTriggerVariables = !!triggerVariables && triggerVariables.length > 0
  const variables = useMemo(
    () =>
      hasTriggerVariables
        ? triggerVariables!.map(name => ({
            name,
            description: 'From the selected trigger',
          }))
        : getDialOutVariables(triggerType),
    [triggerType, triggerVariables, hasTriggerVariables],
  )

  // Clear variableName when the trigger changes and the current value is no
  // longer valid. Skip while the list is empty — that also means "not loaded
  // yet" (trigger variables arrive async), and clearing then would wipe a
  // persisted selection on reload.
  useEffect(() => {
    if (variables.length === 0) return
    const current = getValues('dialOutSettings.variableName')
    if (current && !variables.some(v => v.name === current)) {
      setValue('dialOutSettings.variableName', null, { shouldDirty: true })
    }
  }, [variables, getValues, setValue])

  const noVariables = variables.length === 0
  const hint =
    !hasTriggerVariables && !triggerType
      ? 'Set a trigger on this agent to see available phone variables.'
      : noVariables
        ? 'The selected trigger has no phone-compatible variables.'
        : null

  return (
    <FormField
      control={control}
      name="dialOutSettings.variableName"
      render={({ field }) => (
        <FormItem>
          <Select
            value={field.value || undefined}
            disabled={noVariables}
            onValueChange={val => {
              field.onChange(val)
              void trigger('dialOutSettings')
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a variable…" />
            </SelectTrigger>
            <SelectContent>
              {variables.map(v => (
                <SelectItem key={v.name} value={v.name}>
                  {v.name}
                  <span className="ml-2 text-xs text-muted-foreground">
                    — {v.description}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hint && <FormDescription>{hint}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
